import * as React from "react";

import IconButton from "@mui/material/IconButton";

import Dialog from "@mui/material/Dialog";
import BackupIcon from "@mui/icons-material/Backup";
import Typography from "@mui/material/Typography";
import CardMedia from "@mui/material/CardMedia";

import { useContext } from "react";

import NewRadioSelector from "../../components/NewRadioSelector.js";
import characteristicsList from "../../components/static/editorcharacteristics.json";
import { UpdateSlugPerson } from "../../administration/SlugPersonManagement";
import { CognitoUserContext } from "../../contexts/cognitoUserContext.js";
import { userIsModerator } from "../../services/user/cognitoService";
import { useSearch } from "../../contexts/searchContext";
import { GenerateCharacteristics } from "./CharacteristicsGenerator";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import Box from "@mui/material/Box";
function SimpleDialog(props) {
  const { onClose, map, open, person, cognitoUser, characteristicsSchema } =
    props;
  let p = person.person; // why is this wrapped?

  // Create a local state copy of the map so updates persist
  const [localMap, setLocalMap] = React.useState(new Map(map));

  const handleClose = () => {
    onClose();
  };

  // Here we handle the api call to update the person
  const handleUpdate = () => {
    const updatedPerson = updatePersonAttributes(localMap, p);
    UpdateSlugPerson(cognitoUser, updatedPerson);
    onClose();
  };

  function onCharacteristicsUpdate(event, enumType, enumValue) {
    // Update the map with the new value
    setLocalMap(prev => {
      const newMap = new Map(prev);
      newMap.set(enumType, enumValue);
      return newMap;
    });
  }

  // Take the map of attributes and update the person accordingly
  function updatePersonAttributes(map, person) {
    for (let [key, value] of map) {
      switch (key) {
        case "Gender":
          person.gender = value;
          break;
        case "Party":
          person.party = value;
          break;
        case "PersonType":
          person.persontype = value;
          break;
        case "Glasses":
          person.glasses = value;
          break;
        case "SkinTone":
          person.skintone = value;
          break;
        case "FacialHair":  // Fixed: was "facialHair" (camelCase)
          person.facialhair = value;
          break;
        case "HairColor":
          person.haircolor = value;
          break;
        case "HairStyle":
          person.hairstyle = value;
          break;
        case "AgeGroup":
          person.agegroup = value;
          break;
        default:
      }
    }
    return person;
  }


  return (
    <Dialog
      onClose={handleClose}
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      {/* Fixed Header: Image + Name + Save Button */}
      <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <CardMedia
            component="img"
            sx={{
              width: 120,
              height: 120,
              borderRadius: 1,
              objectFit: 'cover'
            }}
            image={p.image}
            alt={`${p.first_name} ${p.last_name}`}
          />

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {p.first_name} {p.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {p.external_id}
            </Typography>
          </Box>

          <IconButton
            onClick={handleUpdate}
            color="success"
            sx={{
              bgcolor: 'success.light',
              '&:hover': { bgcolor: 'success.main', color: 'white' }
            }}
          >
            <BackupIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Characteristics List */}
      <Box
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          flexGrow: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        {characteristicsList.map((characteristic) => (
          <NewRadioSelector
            key={characteristic}
            enumType={characteristic}
            enumList={characteristicsSchema[characteristic]}
            initValue={localMap.get(characteristic)}
            selectionRequired={true}
            selectorSize={40}
            disabled={false}
            enumMap={localMap}
            onEvent={onCharacteristicsUpdate}
          />
        ))}
      </Box>
    </Dialog>
  );
}

export default function CharacteristicsEditor({ person, open: externalOpen, onClose: externalOnClose }) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const { cognitoUser } = useContext(CognitoUserContext);
  //  const { enumMap, setEnumMap } = useContext(PersonEditorContext); // Get the current value of the map of enums
  const { selectedDataset } = useSearch();
  const characteristicsSchema = GenerateCharacteristics(selectedDataset);

  // Normalize person object - if it's already wrapped, use as-is, otherwise wrap it
  const personObj = person.person ? person : { person };
  const m = personToAttributeMap(personObj);
  const isModerator = userIsModerator(cognitoUser);

  // Use external props if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const isControlled = externalOpen !== undefined;

  if (!isModerator) {
    return null;
  }

  //const [selectedValue, setSelectedValue] =
  // const [selectedValue, setSelectedValue] = React.useState(map);
  const handleClickOpen = () => {
    if (!isControlled) {
      setInternalOpen(true);
    }
  };
  const handleClose = (value) => {
    if (isControlled && externalOnClose) {
      externalOnClose(value);
    } else {
      setInternalOpen(false);
    }
  };

  // If controlled externally, just render dialog without button
  if (isControlled) {
    return (
      <SimpleDialog
        person={personObj}
        map={m}
        open={open}
        onClose={handleClose}
        cognitoUser={cognitoUser}
        characteristicsSchema={characteristicsSchema}
      />
    );
  }

  // Otherwise render button + dialog (original behavior)
  return (
    <div>
      <IconButton
        variant="outlined"
        sx={{
          float: "right",
          borderRadius: 10,
          display: "flex",
          gap: 0,
          height: 20,
          fontSize: "0.7rem",
          fontWeight: "bold",
          letterSpacing: ".05rem",
          textTransform: "uppercase",
          color: "#2d3e50",
        }}
        onClick={handleClickOpen}
      >
        <FaceRetouchingNaturalIcon />
      </IconButton>
      <SimpleDialog
        person={personObj}
        map={m}
        open={open}
        onClose={handleClose}
        cognitoUser={cognitoUser}
        characteristicsSchema={characteristicsSchema}
      />
    </div>
  );
}

function personToAttributeMap(person) {
  const p = person.person;
  let map = new Map();
  map.set("Gender", p.gender);
  map.set("Party", p.party);
  map.set("PersonType", p.persontype);
  map.set("Glasses", p.glasses);
  map.set("SkinTone", p.skintone);
  map.set("FacialHair", p.facialhair);
  map.set("HairColor", p.haircolor);
  map.set("HairStyle", p.hairstyle);
  map.set("AgeGroup", p.agegroup);
  map.set("Dataset", p.dataset);
  return map;
}
